import TableTooltip from '@/components/common/tooltip/table-tooltip/TableTooltip';
import useGetOrganizationSports from '@/hooks/queries/sports-api/useGetOrganizationSports';
import usePersistedPaginationFiltersAndSorting from '@/hooks/usePersistedPaginationFiltersAndSorting';
import { TSports } from '@/services/sports';
import { getFeatureFlagValue } from '@/utils/utils';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { EditIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const useServicesCategoriesTable = () => {
  const { t } = useTranslation();
  const { data: sports, isLoading, isError } = useGetOrganizationSports();
  const emptyData = !sports?.length;
  const {
    pagination,
    setPagination,
    filtering,
    setFiltering,
    sorting,
    setSorting
  } = usePersistedPaginationFiltersAndSorting({
    defaultSorting: [{ id: 'name', desc: false }]
  });

  const featureFlagCrudValue = getFeatureFlagValue('crud');

  const columns = useMemo<ColumnDef<TSports>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('sports.name')
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: t('sports.description'),
        cell: ({ row }) => {
          const description = row?.original?.description;
          return (
            <TableTooltip
              text={description || '-'}
              className="w-[12rem] line-clamp-3"
            />
          );
        }
      },
      {
        id: 'id',
        accessorKey: 'id',
        header: t('tables.common.id'),
        cell: ({ row }) => {
          const id = row?.original?.id;
          return <TableTooltip text={id} className="w-[20rem] line-clamp-3" />;
        }
      },
      {
        id: 'actions-edit',
        enableHiding: false,
        cell: ({ row }) => {
          const categoryId = row.original.id;
          if (!featureFlagCrudValue) {
            return undefined;
          }
          return (
            <Link
              to={{
                pathname: `./edit/${categoryId}`
              }}
              className="w-full"
            >
              <EditIcon />
            </Link>
          );
        }
      }
    ],
    [t, featureFlagCrudValue]
  );

  const table = useReactTable({
    data: sports ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    //pagination config
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    //filter config
    onGlobalFilterChange: setFiltering,
    getFilteredRowModel: getFilteredRowModel(),
    //sort config
    enableMultiSort: false,
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      globalFilter: filtering,
      pagination
    }
  });
  return {
    table,
    filtering,
    setFiltering,
    emptyData,
    featureFlagCrudValue,
    isLoading,
    isError
  };
};

export default useServicesCategoriesTable;
